import React, { useEffect, useRef, useState } from "react";
import InnerLayout from "../components/layouts/inner-layout";
import ItemsInputFieldStandalone from "../components/forms/standalone-input-field";
import {
  ArrowLeft,
  Boxes,
  Info,
  Layers,
  PackageSearch,
  SearchIcon,
  ShoppingCart,
  Tag,
  Wallet,
  X,
} from "lucide-react";
import AddButton from "../components/buttons/button";
import FilterIcon from "../assets/icons/filter-icon";
import TabsComponent from "../components/helper-components/tabs-component";
import DatePickerInput from "../components/forms/date-picker";
import { useDispatch } from "react-redux";
import {
  stockDetailsItemViewAction,
  stockDetailsItemsFetchAction,
  stockDetailsWarehouseLocationAction,
} from "../redux/actions/stock-details-audit-action";
import LoadingSpinner from "../components/helper-components/loading-spinner";
import InfiniteScroll from "react-infinite-scroll-component";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import StockDetailsTable from "../components/tables/stock-details-table";
import {
  storesItemParamedAction,
} from "../redux/actions/general-action";
import { isAdmin } from "../redux/config";
import StockDetailsBatchPanel from "../components/inventory/stock-details/stock-details-batch-panel";
import StockDetailsFilter from "../components/inventory/stock-details/stock-details-filter";
import {
  expiryStatusoptions,
  scheduleoptions,
  stockInOutOptions,
} from "../components/helper-components/data";

const SCHEDULE_LABELS = {
  "Schedule A": "RX-A",
  "Schedule B": "RX-B",
  "Schedule C": "RX-C",
  "Schedule D": "RX-D",
  "Schedule H": "RX-H",
  "Schedule AB": "RX-AB",
};

// Used to show the applied filters as removable chips under the search box
const FILTER_FIELDS = [
  { key: "stock_status", options: stockInOutOptions },
  { key: "expiry_status", options: expiryStatusoptions },
  { key: "rx_drugs", options: scheduleoptions },
];

const ScheduleBadge = ({ schedule }) =>
  schedule ? (
    <span className="shrink-0 whitespace-nowrap text-[var(--pill-badge-text-color)] text-[length:var(--pill-badge-fs)] font-[var(--pill-badge-fw)] px-2 py-[2px] rounded-full bg-[var(--pill-badge-red-bg)]">
      {SCHEDULE_LABELS[schedule] || "RX-S"}
    </span>
  ) : null;

const StockStatusBadge = ({ status }) =>
  status ? (
    <span
      className={`shrink-0 whitespace-nowrap px-2 py-[2px] rounded-full font-[var(--status-badge-fw)] text-[length:var(--status-badge-fs)] ${
        status === "In Stock"
          ? "border border-[var(--status-badge-green-border)] bg-[var(--status-badge-green-bg)] text-[var(--status-badge-green-fg)]"
          : "border border-[var(--status-badge-red-border)] bg-[var(--status-badge-red-bg)] text-[var(--status-badge-red-fg)]"
      }`}
    >
      {status}
    </span>
  ) : null;

const InfoTile = ({ label, value, sub, icon: Icon }) => (
  <div className="min-w-0 rounded-lg border border-[var(--layout-border-color)] bg-[var(--dashboard-gray-50)] px-3 py-2">
    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--dashboard-gray-500)]">
      {Icon && <Icon size={13} />}
      {label}
    </div>
    <div
      className="text-base font-bold text-[var(--dashboard-gray-900)] truncate"
      title={value ?? ""}
    >
      {value ?? "--"}
    </div>
    {sub && (
      <div className="text-xs font-semibold text-[var(--dashboard-gray-700)] truncate">
        {sub}
      </div>
    )}
  </div>
);

const formatStockValue = (value) => {
  const num = Number(value);
  if (value === undefined || value === null || value === "") return "₹0";
  return Number.isNaN(num)
    ? value
    : `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const StockDetails = () => {
  const [data, setData] = useState([]);
  const initFilter = {
    search: "",
    stock_status: "",
    expiry_status: "",
    rx_drugs: "",
  };
  const [batchOpen, setBatchOpen] = useState(false);
  const navigate = useNavigate();
  const filterPopupRef = useRef(null);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [showFilter, setShowFilter] = useState(false);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const [currentTab, setCurrentTab] = useState("Batches");
  const [filterData, setFilterData] = useState(initFilter);
  const [selectedItemId, setSelectedItemId] = useState("");
  // Below lg only one pane fits, so we switch between the list and the details
  const [mobileView, setMobileView] = useState("list");

  const productDetails =
    items?.find((item) => item?.item_id === selectedItemId) || {};
  const itemTabs = [
    "Batches",
    "Purchases",
    "Sales",
    "Alternatives",
    "Vendors",
    "Time Line",
    "Other Stores",
    "Warehouse Location",
  ];
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    has_next: false,
    has_previous: false,
  });

  useEffect(() => {
    dispatch(
      stockDetailsItemsFetchAction(
        { page_no: page, ...filterData },
        (data) => {
          setLoading(false);
          const p = data?.data?.pagination;
          const newItems = data?.data?.items || [];

          if (page === 1) {
            setItems(newItems);
            setSelectedItemId(newItems[0]?.item_id);
          } else {
            setItems((prev) => [...prev, ...newItems]); // append for other pages
          }

          setPagination(p);
        },
        () => {
          setLoading(false);
          if (page === 1) setItems([]);
          setData([]);
          setSelectedItemId("");
        }
      )
    );
  }, [page, filterData]);

  // Load more simply increments the page
  const handleLoadMore = () => {
    if (pagination.has_next) {
      setPage((prev) => prev + 1);
    }
  };

  // When filter changes, reset page to 1
  useEffect(() => {
    setPage(1);
  }, [filterData]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        showFilter &&
        filterPopupRef.current &&
        !filterPopupRef.current.contains(event.target)
      ) {
        setShowFilter((prevShowFilter) => !prevShowFilter);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [showFilter]);

  const debouncedSearch = useRef(
    debounce((val) => {
      setFilterData((prev) => ({ ...prev, search: val }));
    }, 400)
  ).current;
  //ITEM DETAILS FILTER

  const [currentBatch, setCurrentBatch] = useState({});
  const [dataCount, setDataCount] = useState(0);
  const [dataLoading, setDataLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const paginationProps = {
    pageSize: pageSize,
    setPageSize: setPageSize,
    dataCount: dataCount,
    setCurrentPage: setCurrentPage,
    currentPage: currentPage,
  };
  const initItemDetailsFilter = {
    tab_type: "batches",
    item_id: selectedItemId,
    search: "",
    page_no: 1,
    sortBy: "",
    sortOrder: "",
    fromDate: "",
    toDate: "",
    state: "",
    district: "",
    City: "",
  };
  const [itemDetailsFilter, setItemDetailsFilter] = useState(
    initItemDetailsFilter
  );
  useEffect(() => {
    // Other Stores is fetched by its own effect below
    if (itemDetailsFilter?.tab_type === "otherstores") return;
    if (selectedItemId) {
      setDataLoading(true);
      if (itemDetailsFilter?.tab_type === "warehouselocation") {
        dispatch(
          stockDetailsWarehouseLocationAction(
            { item_id: selectedItemId },
            onWarehouseLocationSuccess,
            onError
          )
        );
        return;
      }
      dispatch(
        stockDetailsItemViewAction(
          {
            tab_type: itemDetailsFilter?.tab_type || "",
            item_id: selectedItemId,
            search: itemDetailsFilter?.search || "",
            page_no: paginationProps?.currentPage || 1,
            sortBy: itemDetailsFilter?.sortBy || "",
            sortOrder: itemDetailsFilter?.sortOrder || "",
            fromDate: itemDetailsFilter?.fromDate || null,
            toDate: itemDetailsFilter?.toDate || null,
            state: itemDetailsFilter?.state || "",
            district: itemDetailsFilter?.district || "",
            city: itemDetailsFilter?.city || "",
          },
          onSuccess,
          onError
        )
      );
    } else {
      setData([]);
    }
  }, [selectedItemId, itemDetailsFilter, currentPage]);

  const onSuccess = (data) => {
    setDataLoading(false);
    setDataCount(data?.data?.pagination?.total_items || 0);
    setData(
      data?.data?.batchlist ||
        data?.data?.purchaselist ||
        data?.data?.saleslist ||
        data?.data?.alternativelist ||
        data?.data?.vendorlist ||
        data?.data?.timeLinesData ||
        data?.data ||
        []
    );
  };
  const onError = (data) => {
    setDataLoading(false);
    setData([]);
  };
  const onWarehouseLocationSuccess = (data) => {
    const rows = data?.data || [];
    setDataLoading(false);
    setDataCount(rows.length);
    setData(
      rows.map((row) => ({
        ...row,
        is_expired_bin_location: row?.is_expired_bin_location ? "Yes" : "No",
        is_non_sellable_bin_location: row?.is_non_sellable_bin_location
          ? "Yes"
          : "No",
      }))
    );
  };

  // OTHER STORES
  const storesItemDebounceRef = useRef(null);
  useEffect(() => {
    if (itemDetailsFilter?.tab_type !== "otherstores" || !selectedItemId) {
      return;
    }
    if (storesItemDebounceRef.current) {
      clearTimeout(storesItemDebounceRef.current);
    }
    storesItemDebounceRef.current = setTimeout(() => {
      setDataLoading(true);
      // excluded / pos_id are set in the saga based on isAdmin
      dispatch(
        storesItemParamedAction(
          {
            item_id: selectedItemId,
            page: currentPage || 1,
            limit: pageSize || 10,
          },
          onOtherStoresSuccess,
          onError
        )
      );
    }, 300);
    return () => {
      if (storesItemDebounceRef.current) {
        clearTimeout(storesItemDebounceRef.current);
      }
    };
  }, [
    itemDetailsFilter?.tab_type,
    selectedItemId,
    currentPage,
    pageSize,
  ]);
  const onOtherStoresSuccess = (data) => {
    const rows = data?.data?.stores || [];
    setDataLoading(false);
    setDataCount(data?.data?.pagination?.total || 0);
    setData(rows);
  };

  const [BatchesHeading, setBatchesHeading] = useState([]);
  const [purchaseHeading, setpurchaseHeading] = useState([]);
  const [salesHeading, setSalesHeading] = useState([]);
  const [alternativesHeading, setAlternativesHeading] = useState([]);
  const [vendorsHeading, setVendorsHeading] = useState([]);
  const [timeLineHeading, setTimeLineHeading] = useState([]);

  useEffect(() => {
    const data = localStorage.getItem("ModPermission");
    if (data) {
      const parsed = JSON.parse(data);
      const stock_details_batch_tab = parsed.find(
        (obj) => obj?.stock_details_batch_tab
      );
      const stock_details_purchase_tab = parsed.find(
        (obj) => obj?.stock_details_purchase_tab
      );
      const stock_details_sales_tab = parsed.find(
        (obj) => obj?.stock_details_sales_tab
      );
      const stock_details_alternative_tab = parsed.find(
        (obj) => obj?.stock_details_alternative_tab
      );
      const stock_details_vendors_tab = parsed.find(
        (obj) => obj?.stock_details_vendors_tab
      );
      const stock_details_timeline_tab = parsed.find(
        (obj) => obj?.stock_details_timeline_tab
      );
      if (stock_details_batch_tab?.stock_details_batch_tab) {
        setBatchesHeading(stock_details_batch_tab.stock_details_batch_tab);
      }
      if (stock_details_purchase_tab.stock_details_purchase_tab) {
        setpurchaseHeading(
          stock_details_purchase_tab.stock_details_purchase_tab
        );
      }
      if (stock_details_sales_tab.stock_details_sales_tab) {
        setSalesHeading(stock_details_sales_tab.stock_details_sales_tab);
      }

      if (stock_details_alternative_tab?.stock_details_alternative_tab) {
        setAlternativesHeading(
          stock_details_alternative_tab?.stock_details_alternative_tab
        );
      }
      if (stock_details_vendors_tab.stock_details_vendors_tab) {
        setVendorsHeading(stock_details_vendors_tab.stock_details_vendors_tab);
      }
      if (stock_details_timeline_tab?.stock_details_timeline_tab) {
        setTimeLineHeading(
          stock_details_timeline_tab.stock_details_timeline_tab
        );
      }
    }
  }, []);

  const activeFilters = FILTER_FIELDS.filter((f) => filterData?.[f.key]).map(
    (f) => ({
      key: f.key,
      label:
        f.options.find((o) => o.value === filterData[f.key])?.label ||
        filterData[f.key],
    })
  );

  const handleTabClick = (item) => {
    setCurrentTab(item);
    setItemDetailsFilter({
      ...itemDetailsFilter,
      tab_type:
        item === "Batches"
          ? "batches"
          : item === "Purchases"
          ? "purchase"
          : item === "Sales"
          ? "sales"
          : item === "Alternatives"
          ? "alternative"
          : item === "Vendors"
          ? "vendors"
          : item === "Time Line"
          ? "timeline"
          : item === "Other Stores"
          ? "otherstores"
          : item === "Warehouse Location"
          ? "warehouselocation"
          : "",
      fromDate: "",
    });
    setCurrentPage(1);
  };

  const handleRowClick = (rowData) => {
    if (currentTab === "Batches") {
      setCurrentBatch(rowData);
      setBatchOpen(true);
    } else if (currentTab === "Purchases") {
      navigate(`/view-purchase-invoice/${rowData?.poit_id}`);
    } else if (currentTab === "Sales") {
      navigate(`/view-sales-invoice/${rowData?.soit_id}`);
    } else if (currentTab === "Alternatives") {
      navigate(`/view-product/${rowData?.item_id}`);
    } else if (currentTab === "Vendors") {
      if (isAdmin) {
        navigate(`/admin/view-customer/${rowData?.cmr_id}`);
      } else {
        navigate(`/view-customer/${rowData?.cmr_id}`);
      }
    } else if (currentTab === "Time Line") {
      if (rowData?.transaction_type === "Sales Invoice") {
        navigate(`/view-sales-invoice/${rowData?.transaction_ref_id}`);
      } else if (rowData?.transaction_type === "Purchase Return") {
        navigate(`/view-purchase-return/${rowData?.transaction_ref_id}`);
      } else if (rowData?.transaction_type === "Purchase Invoice") {
        navigate(`/view-purchase-invoice/${rowData?.transaction_ref_id}`);
      } else if (rowData?.transaction_type === "Purchase Credit") {
        navigate(`/view-purchase-credit/${rowData?.transaction_ref_id}`);
      } else if (rowData?.transaction_type === "Sales Return") {
        navigate(`/view-sales-return/${rowData?.transaction_ref_id}`);
      } else if (rowData?.transaction_type === "Sales Credit") {
        navigate(`/view-sales-credit/${rowData?.transaction_ref_id}`);
      } else if (rowData?.transaction_type === "Sales Delivery") {
        navigate(`/view-delivery/${rowData?.transaction_ref_id}`);
      } else if (rowData?.transaction_type === "Stock In") {
        navigate(`/view-stock-transfer/${rowData?.transaction_ref_id}`);
      } else if (rowData?.transaction_type === "Stock Out") {
        navigate(`/view-stock-transfer/${rowData?.transaction_ref_id}`);
      } else if (rowData?.transaction_type === "GRN") {
        navigate(`/view-goods/${rowData?.transaction_ref_id}`);
      } else if (rowData?.transaction_type === "Billing") {
        navigate(`/view-bill/${rowData?.transaction_ref_id}`);
      } else {
        // handle unknown or unsupported transaction types
      }
    } else if (currentTab === "Other Stores") {
      navigate("/add-stock-transfer");
    }
  };

  return (
    <InnerLayout title="Stock Details" backBtn={true}>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="w-full h-full flex overflow-hidden">
          {/* Product list */}
          <aside
            className={`${
              mobileView === "detail" ? "hidden" : "flex"
            } lg:flex flex-col w-full lg:w-[340px] xl:w-[380px] shrink-0 min-h-0 bg-white border-r border-r-[var(--layout-border-color)]`}
          >
            <div className="shrink-0 px-4 pt-4 pb-3 border-b border-b-[var(--layout-border-color)]">
              <h2
                className="font-[var(--stock-det-head-fw)]
            text-[length:var(--stock-det-head-fs)]
            text-[var(--stock-det-head-fg)]"
              >
                Stock In Hand
              </h2>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <InfoTile
                  label="SKUs"
                  value={pagination?.total_items ?? 0}
                  icon={Boxes}
                />
                <InfoTile
                  label="Stock Value"
                  value={formatStockValue(pagination?.stock_value)}
                  icon={Wallet}
                />
              </div>

              {/* The popover lives outside any scroll container, so it opens fully over the list */}
              <div
                ref={filterPopupRef}
                className="relative flex items-center gap-2 mt-3"
              >
                <div className="flex-1 min-w-0">
                  <ItemsInputFieldStandalone
                    label="Name"
                    leftIcon={
                      <SearchIcon
                        className="text-[var(--stand-alone-place-color)]"
                        size={"15px"}
                      />
                    }
                    isLabelReq={false}
                    placeholder="Search products"
                    value={filterData.name}
                    onChange={(e) => debouncedSearch(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  aria-expanded={showFilter}
                  onClick={() => {
                    setShowFilter((prevShowFilter) => !prevShowFilter);
                  }}
                  className={`shrink-0 flex items-center gap-2 h-[44px] px-3 rounded-[8px] border whitespace-nowrap transition-colors
            font-[var(--general-button-fw)]
            text-[length:var(--general-button-fs)] ${
              showFilter || activeFilters.length > 0
                ? "border-[var(--primary)] bg-[var(--row-selection-color)] text-[var(--primary)]"
                : "border-[var(--general-button-border)] bg-[var(--general-button-bg)] text-[var(--general-button-fg)]"
            }`}
                >
                  <FilterIcon width="14px" />
                  Filter
                  {activeFilters.length > 0 && (
                    <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--primary)] text-white text-[11px] leading-none flex items-center justify-center">
                      {activeFilters.length}
                    </span>
                  )}
                </button>
                {showFilter && (
                  <div className="absolute right-0 top-full mt-2 z-40">
                    <StockDetailsFilter
                      initFilter={initFilter}
                      setPagination={setPagination}
                      filterData={filterData}
                      setFilterData={setFilterData}
                      onClose={() => setShowFilter(false)}
                    />
                  </div>
                )}
              </div>

              {activeFilters.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {activeFilters.map((f) => (
                    <span
                      key={f.key}
                      className="inline-flex items-center gap-1 h-7 pl-3 pr-1 rounded-full text-xs font-medium bg-[var(--row-selection-color)] text-[var(--primary)]"
                    >
                      {f.label}
                      <button
                        type="button"
                        aria-label={`Remove ${f.label} filter`}
                        onClick={() =>
                          setFilterData({ ...filterData, [f.key]: "" })
                        }
                        className="p-0.5 rounded-full hover:bg-white"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setFilterData({
                        ...initFilter,
                        search: filterData?.search || "",
                      })
                    }
                    className="text-xs font-medium text-[var(--stock-det-item-con-lab-fg)] hover:text-[var(--primary)]"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            <div
              id="scrollableDiv"
              className="flex-1 min-h-0 overflow-y-auto scrollbar-rounded"
            >
              {items?.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center gap-1 px-6 py-10 text-center">
                  <PackageSearch
                    size={36}
                    className="text-[var(--stock-det-item-con-lab-fg)] mb-2"
                  />
                  <p className="text-sm font-semibold text-[var(--stock-det-head-fg)]">
                    No products found
                  </p>
                  <p className="text-xs text-[var(--stock-det-item-con-lab-fg)]">
                    Try a different search or clear the filters.
                  </p>
                </div>
              ) : (
                <InfiniteScroll
                  className="flex flex-col gap-2 p-3"
                  style={{ overflow: "visible" }}
                  dataLength={items.length} // length of the current items
                  next={handleLoadMore} // function to fetch more data
                  hasMore={pagination.has_next} // whether more data is available
                  loader={
                    <p className="py-3 text-center text-xs text-[var(--stock-det-item-con-lab-fg)]">
                      Loading more…
                    </p>
                  }
                  scrollableTarget="scrollableDiv"
                >
                  {items?.map((item) => {
                    const isSelected = selectedItemId === item.item_id;
                    return (
                      <button
                        type="button"
                        key={item.item_id}
                        onClick={() => {
                          setSelectedItemId(item.item_id);
                          setMobileView("detail");
                        }}
                        className={`w-full text-left rounded-lg border p-3 transition-colors ${
                          isSelected
                            ? "border-[var(--primary)] bg-[var(--stock-det-item-con-sel-bg)] shadow-[inset_4px_0_0_var(--primary)]"
                            : "border-[var(--stock-det-item-con-border)] bg-white hover:border-[var(--primary)] hover:bg-[var(--stock-det-item-con-sel-bg)]"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <h3
                            title={item?.item_name}
                            className="text-sm font-medium text-[var(--stock-det-head-fg)] line-clamp-2 break-words"
                          >
                            {item?.item_name}
                          </h3>
                          <ScheduleBadge schedule={item?.item_schedule} />
                        </div>
                        <div className="mt-2 flex items-end justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <StockStatusBadge status={item?.stock_status} />
                            <span className="truncate text-xs text-[var(--dashboard-gray-500)]">
                              UOM:{" "}
                              <span className="font-semibold text-[var(--dashboard-gray-900)]">
                                {item?.uom_group_name || "--"}
                              </span>
                            </span>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--dashboard-gray-500)]">
                              Avail. Qty
                            </div>
                            <div className="text-base font-bold leading-tight text-[var(--dashboard-gray-900)]">
                              {item?.item_quantity ?? "--"}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </InfiniteScroll>
              )}
            </div>
          </aside>

          {/* Product details */}
          <section
            className={`${
              mobileView === "list" ? "hidden" : "flex"
            } lg:flex flex-1 min-w-0 min-h-0 flex-col overflow-y-auto scrollbar-rounded`}
          >
            <div className="lg:hidden shrink-0 px-4 pt-4">
              <button
                type="button"
                onClick={() => setMobileView("list")}
                className="inline-flex items-center gap-1 text-sm font-medium text-[var(--primary)]"
              >
                <ArrowLeft size={16} /> All products
              </button>
            </div>

            {productDetails?.item_name ? (
              <>
                <div className="shrink-0 px-4 sm:px-6 pt-4 sm:pt-5">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2
                          className="font-[var(--stock-det-head-fw)]
            text-[length:var(--stock-det-head-fs)]
            text-[var(--stock-det-head-fg)] break-words"
                        >
                          {productDetails?.item_name}
                        </h2>
                        <StockStatusBadge status={productDetails?.stock_status} />
                        <ScheduleBadge schedule={productDetails?.item_schedule} />
                      </div>
                      {(productDetails?.item_generic_name ||
                        productDetails?.item_manufacturer) && (
                        <p
                          className="font-[var(--stock-det-desc-fw)]
            text-[length:var(--stock-det-desc-fs)]
            text-[var(--stock-det-desc-fg)] mt-[0.2rem]"
                        >
                          {[
                            productDetails?.item_generic_name,
                            productDetails?.item_manufacturer,
                          ]
                            .filter(Boolean)
                            .join(" - ")}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 items-center shrink-0">
                      <AddButton
                        onclkFn={() => {
                          navigate(`/add-audit`);
                        }}
                        prefixIcon={false}
                        text="Add To Audit"
                        className="
flex items-center justify-center px-[20px] h-[40px] whitespace-nowrap rounded-md gap-[8px]
bg-[var(--primary-button-bg-color)] cursor-pointer
text-[var(--primary-button-fg-color)]
border border-[var(--primary-button-bg-color)]
hover:bg-[var(--primary-button-hover-color)]
text-[length:var(--primary-button-create-fs)]
font-[var(--primary-button-font-weight)]"
                        type="submit"
                      />
                      <AddButton
                        onclkFn={() => {
                          navigate(`/view-product/${productDetails?.item_id}`);
                        }}
                        icon={<Info size={16} />}
                        text="Product Info"
                        className={`flex items-center px-[12px] h-[40px] whitespace-nowrap rounded-md gap-[8px]
            bg-[var(--general-button-bg)] cursor-pointer
            border border-[var(--general-button-border)]
            hover:bg-[var(--general-button-bg)] text-[var(--general-button-fg)] font-[var(--general-button-fw)]`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
                    <InfoTile
                      label="Available Qty"
                      value={productDetails?.item_quantity}
                      icon={Boxes}
                    />
                    <InfoTile
                      label="UOM"
                      value={productDetails?.uom_group_name}
                      icon={Layers}
                    />
                    <InfoTile
                      label="Purchase UOM"
                      value={productDetails?.purchase_pack_name || "--"}
                      sub={`Qty: ${productDetails?.purchase_uom_qty || "--"}`}
                      icon={ShoppingCart}
                    />
                    <InfoTile
                      label="Sales UOM"
                      value={productDetails?.sales_pack_name || "--"}
                      sub={`Qty: ${productDetails?.sales_uom_qty || "--"}`}
                      icon={Tag}
                    />
                  </div>
                </div>

                <div className="shrink-0 px-4 sm:px-6 mt-2 flex flex-col xl:flex-row xl:items-center gap-x-4">
                  <div className="flex-1 min-w-0">
                    <TabsComponent
                      handleClickfn={handleTabClick}
                      itemTabs={itemTabs}
                      setCurrentTab={setCurrentTab}
                      currentTab={currentTab}
                    />
                  </div>
                  {(currentTab === "Purchases" || currentTab === "Time Line") && (
                    <div className="flex items-center gap-2 flex-wrap pb-3 xl:pb-0">
                      <div style={{ height: "32px" }}>
                        <DatePickerInput
                          placeholder="From Date"
                          onChange={(date) => {
                            setItemDetailsFilter({
                              ...itemDetailsFilter,
                              fromDate: date,
                            });
                          }}
                          value={itemDetailsFilter?.fromDate}
                          rangeRole="start"
                          rangeEnd={itemDetailsFilter?.toDate}
                        />
                      </div>
                      <div style={{ height: "32px" }}>
                        <DatePickerInput
                          placeholder="To Date"
                          onChange={(date) => {
                            setItemDetailsFilter({
                              ...itemDetailsFilter,
                              toDate: date,
                            });
                          }}
                          value={itemDetailsFilter?.toDate}
                          rangeRole="end"
                          rangeStart={itemDetailsFilter?.fromDate}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Fills the remaining height on desktop; keeps a usable height when the pane scrolls on small screens */}
                <div className="flex-1 min-h-[420px] mt-2 pb-4">
                  {dataLoading ? (
                    <LoadingSpinner />
                  ) : (
                    <StockDetailsTable
                      onClickItemFn={handleRowClick}
                      setFilterData={setItemDetailsFilter}
                      filterData={itemDetailsFilter}
                      label="Data"
                      paginationProps={paginationProps}
                      tableHeading={
                        currentTab === "Batches"
                          ? BatchesHeading
                          : currentTab === "Purchases"
                          ? purchaseHeading
                          : currentTab === "Sales"
                          ? salesHeading
                          : currentTab === "Alternatives"
                          ? alternativesHeading
                          : currentTab === "Vendors"
                          ? vendorsHeading
                          : currentTab === "Time Line"
                          ? timeLineHeading
                          : currentTab === "Other Stores"
                          ? otherStoresHeading
                          : currentTab === "Warehouse Location"
                          ? warehouseLocationHeading
                          : []
                      }
                      tableBodyData={data || []}
                    />
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center gap-1 p-6 text-center">
                <PackageSearch
                  size={40}
                  className="text-[var(--stock-det-item-con-lab-fg)] mb-2"
                />
                <p className="text-sm font-semibold text-[var(--stock-det-head-fg)]">
                  Select a product
                </p>
                <p className="text-xs text-[var(--stock-det-item-con-lab-fg)]">
                  Its batches, purchases, sales and more will show here.
                </p>
              </div>
            )}
          </section>

          <StockDetailsBatchPanel
            setCurrentBatch={setCurrentBatch}
            data={data}
            currentBatch={currentBatch}
            setOpen={setBatchOpen}
            open={batchOpen}
            productDetails={productDetails}
          />
        </div>
      )}
    </InnerLayout>
  );
};

export default StockDetails;

const otherStoresHeading = [
  { label: "STORE CODE", value: "store_code", minWidth: 100 },
  { label: "STORE ADDRESS", value: "shop_address", minWidth: 220 },
  { label: "STORE TYPE", value: "store_type", minWidth: 100 },
  {
    label: "AVAIL. QTY",
    value: "total_quantity",
    minWidth: 80,
    alignment: "right",
  },
  {
    label: "NO. OF BATCHES",
    value: "batch_count",
    minWidth: 100,
    alignment: "right",
  },
  { label: "", value: "actions" },
];

const warehouseLocationHeading = [
  { label: "ITEM CODE", value: "item_code", minWidth: 100 },
  { label: "ITEM NAME", value: "item_name", minWidth: 180 },
  { label: "BATCH NO.", value: "item_batch_number", minWidth: 120 },
  { label: "EXPIRY DATE", value: "item_exp_date", minWidth: 140 },
  { label: "BIN LOCATION", value: "bin_location_no", minWidth: 120 },
  {
    label: "QTY",
    value: "item_quantity",
    minWidth: 80,
    alignment: "right",
  },
  {
    label: "EXPIRED BIN",
    value: "is_expired_bin_location",
    minWidth: 100,
    alignment: "center",
  },
  {
    label: "NON-SELLABLE BIN",
    value: "is_non_sellable_bin_location",
    minWidth: 130,
    alignment: "center",
  },
];

// const BatchesHeading = [
//   {
//     label: "BATCH AGE",
//     value: "batch_age", // new
//     display: false,
//   },
//   {
//     label: "BATCH NO.",
//     value: "item_batch_number",
//     display: false,
//   },
//   {
//     label: "PURCHASE INVOICE NUMBER", // new
//     value: "poit_invoice_number",
//     display: false,
//   },
//   {
//     label: "EXPIRY DATE",
//     value: "item_exp_date",
//     display: true,
//   },
//   {
//     label: "STATUS",
//     value: "stock_status",
//     display: false,
//   },
//   {
//     label: "MRP ₹",
//     value: "item_mrp_price",
//     display: false,
//   },
//   {
//     label: "FINAL PR ₹",
//     value: "item_batch_purchase_rate",
//     display: false,
//   },
//   {
//     label: "SALE RATE ₹",
//     value: "item_batch_sales_rate",
//     display: false,
//   },
//   {
//     label: "MARGIN%",
//     value: "item_batch_margin_percentage",
//     display: false,
//   },
//   {
//     label: "AVAIL. QTY.",
//     value: "item_quantity",
//     display: true,
//   },
//   {
//     label: "PURCHASED QTY",
//     value: "purchase_quantity",
//     display: true,
//   },
//   {
//     label: "NO. OF BILLED TRANSACTIONS",
//     value: "no_of_billed_transaction",
//     display: false,
//   },
//   {
//     label: "NO. OF RETURN TRANSACTIONS",
//     value: "no_of_return_transaction",
//     display: false,
//   },
//   {
//     label: "UOM",
//     value: "item_uom",
//     display: false,
//   },
//   {
//     label: "BIN LOCATION",
//     value: "to_bin_location",
//     display: false,
//   },
//   {
//     label: "CREATED ON",
//     value: "created_date",
//     display: false,
//     type: "date",
//   },
//   {
//     label: "CREATED BY",
//     value: "created_by",
//     display: false,
//   },
//   {
//     label: "UPDATED BY",
//     value: "item_uom",
//     display: false,
//   },
//   {
//     label: "",
//     value: "actions",
//   },
// ];

// const purchaseHeading = [
//   { label: "PURCHASED AGE", value: "purchase_age", display: false },
//   { label: "INVOICE DATE", value: "created_date", display: true, type: "date" },
//   { label: "VENDOR NAME", value: "vendor_name", display: false },
//   { label: "INVOICE NUMBER", value: "poit_invoice_number", display: false },
//   { label: "BATCH NO.", value: "item_batch_number", display: false },
//   { label: "EXPIRY DATE", value: "item_exp_date", display: false },
//   { label: "MRP ₹", value: "item_mrp_price", display: false },
//   { label: "FINAL PR ₹", value: "item_batch_purchase_rate", display: false },
//   { label: "DISC%", value: "item_batch_discount_percentage", display: false },
//   { label: "MARGIN%", value: "item_batch_margin_percentage", display: false },
//   {
//     label: "PROFIT MARGIN VALUE",
//     value: "profit_margin_value",
//     display: false,
//   },
//   { label: "PURCHASE QTY", value: "purchase_quantity", display: false },
//   { label: "TOTAL QTY", value: "total_quantity", display: false },
//   { label: "GST%", value: "item_total_tax_percentage", display: false },
//   { label: "GST AMOUNT", value: "item_tax_amount", display: false },
//   { label: "TOTAL AMOUNT", value: "item_total_amount", display: false },
//   { label: "", value: "actions", display: false },
// ];

// const salesHeading = [
//   { label: "INVOICE DATE", value: "created_date", display: true, type: "date" },
//   { label: "INVOICE NO", value: "soit_invoice_number", display: false },
//   { label: "SOLD TO", value: "cmr_name", display: false },
//   { label: "BATCH NO.", value: "item_batch_number", display: false },
//   { label: "PRODUCT", value: "item_name", display: false },
//   { label: "MRP ₹", value: "item_mrp_price", display: false },
//   { label: "SALES RATE", value: "item_batch_sales_rate", display: false },
//   { label: "DISC%", value: "item_batch_discount_percentage", display: false },
//   { label: "GST%", value: "item_batch_tax_percentage", display: false },
//   { label: "TOTAL AMOUNT", value: "item_total_amount", display: false },
//   // { label: "PROFIT", value: "profit", display: false },
//   { label: "MARGIN%", value: "sot_margin_percentage", display: false },
//   { label: "QTY SOLD", value: "sold_quantity", display: false },
//   { label: "SALES BY", value: "sales_by", display: false },
//   { label: "RACK LOCATION", value: "item_rack_location", display: false },
//   { label: "PAYMENT METHOD", value: "soit_payment_method", display: false },
//   { label: "RETURN STATUS", value: "sot_return_status", display: false },
//   { label: "", value: "actions", display: false },
// ];

// const alternativesHeading = [
//   { label: "ID", value: "item_id", display: false },
//   { label: "PRODUCT CODE", value: "item_code", display: false },
//   { label: "PRODUCT NAME", value: "item_name", display: false },
//   { label: "BATCH NO.", value: "item_batch_number", display: false },
//   { label: "BIN", value: "to_bin_location", display: false },
//   { label: "EXPIRY", value: "item_exp_date", display: false },
//   { label: "STATUS", value: "stock_status", display: false },
//   { label: "SELLABLE QTY", value: "item_sellable_quantity", display: false },
//   {
//     label: "NON-SELLABLE QTY",
//     value: "item_non_sellable_quantity",
//     display: false,
//   },
//   { label: "MRP", value: "item_mrp_price", display: false },
//   { label: "SALE RATE", value: "item_batch_sales_rate", display: false },
//   { label: "MARGIN%", value: "item_batch_margin_percentage", display: false },
//   { label: "PRICE DIFFERENCE", value: "price_diffrence", display: true },
//   { label: "VENDOR", value: "cmr_name", display: false },
//   { label: "Actions", value: "action", display: false }, // Set false to keep consistency
// ];

// const vendorsHeading = [
//   { label: "VENDOR", value: "cmr_first_name", display: false },
//   { label: "GSTIN", value: "cmr_gstin", display: false },
//   { label: "CONTACT", value: "cmr_phone_number", display: false },
//   // { label: "CREDIT TIME (DAYS)", value: "credit_days", display: false },
//   { label: "CREDIT LIMIT ₹", value: "credit_limit", display: false },
//   {
//     label: "LAST TRANSACTION DATE",
//     value: "last_transaction_date",
//     display: false,
//     type: "date",
//   },
//   { label: "Actions", value: "action", display: false }, // Set false to keep consistency
// ];

// const timeLineHeading = [
//   {
//     label: "TXN DATE",
//     value: "transaction_date",
//     display: false,
//     type: "date",
//   },
//   { label: "TXN TYPE", value: "transaction_type", display: false },
//   { label: "TXN NO", value: "invoice_number", display: false },
//   { label: "PARTY NAME", value: "vendor_name", display: false },
//   {
//     label: "LINKED INVOICE NO",
//     value: "linked_invoice_number",
//     display: false,
//   },
//   { label: "BATCH NO", value: "batch_number", display: false },
//   { label: "QTY IN", value: "quantity_in", display: false },
//   { label: "QTY OUT", value: "quantity_out", display: false },
//   { label: "AVAILABLE QTY", value: "quantity", display: false },
//   { label: "FINAL RATE", value: "final_rate", display: false },
//   { label: "DISC %", value: "discount_percentage", display: false },
//   { label: "GST %", value: "tax_percentage", display: false },
//   { label: "BIN LOCATION", value: "bin_location", display: false },
//   { label: "CREATED BY", value: "created_by", display: false },
//   { label: "EXPIRY DATE", value: "expiry_date", display: false, type: "date" },
//   { label: "Actions", value: "action", display: false }, // Set false to keep consistency
// ];

// const otherStoresheading = [
//   { label: "Store Code", value: "store_code", display: false },
//   { label: "Store Address", value: "store_address", display: false },
//   { label: "Store Type", value: "store_type", display: false },
//   { label: "Product Expiry Status", value: "expiry_status", display: false },
//   { label: "Stock Status", value: "stock_status", display: false },
//   { label: "MRP Range", value: "max_mrp_range", display: false },
//   { label: "Sale Rate Range", value: "sale_rate_range", display: false },
//   { label: "Available Qty", value: "available_qty", display: false },
//   { label: "No. of Batches", value: "batch_count", display: false },
//   // { label: "Actions", value: "action", display: false }, // Set false to keep consistency
// ];
